import { edge, hash, mkTex, polyR, putA } from './tex-canvas';
/* ============================================================
   候選風格的貼圖（硬邊 / 賽璐珞）
   ============================================================

   第三版（實彈／軍械）的貼圖全部是**柔邊漸層**：模糊的火球、柔光的煙、
   拖著長尾的火星。那是寫實 VFX 的語彙，而這個遊戲的場景與機甲是
   **硬邊多面體**。兩種語言疊在一起，畫面就是「一團髒」——
   使用者 2026-08-05 的四項回饋（太糊、風格不合、打擊感不足、沒有記憶點）
   有三項指向同一個原因：**貼圖是柔的**。

   所以這一輪的所有貼圖只有一條規則：

   > **alpha 只有 0 與 1，過渡寬度固定一格。**

   邊界銳利 = 有形狀 = 讀得出設計。而且硬邊的東西即使不透明度拉到 1
   也不會被 UnrealBloom 糊成光暈 —— 只要它走一般混色。
   「不靠泛光也能很重」正是前兩版（動漫 VFX、霓虹幾何）沒做到的事。

   分成兩組：
   - **幾何組**（A 硬邊幾何 / D 硬邊能量共用）：多邊形、三角碎片、針狀切片。
   - **賽璐珞組**（B 二維影格）：手繪感的爆炸剪影、集中線。 */

/* ---------- 幾何組 ---------- */

/** 實心正 n 邊形。**這一版的「核心」不再是柔邊圓球**。

    圓球是寫實 VFX 的預設；多邊形是這個遊戲的形狀語言（場景、機甲、
    敵人全是多面體）。同樣一發槍口，畫成六邊形就會讀成「這台機器打出來的」，
    畫成模糊圓球就會讀成「有東西在發光」。 */
export function texPolyFill(n: number) {
  return mkTex('p2fill' + n, 128, 128, function (d, w) {
    var r = w / 2, aa = 1.5 / r;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var rp = polyR(n, Math.atan2(dy, dx)) * 0.95;
      putA(d, (y * w + x) * 4, edge(rp - rad, aa));
    }
  });
}

/** 實心多邊形**帶**（厚環）。硬邊的衝擊波。

    跟 `vfx-tex.ts` 的 `texPolyRing` 差在厚度與用途：那個是細線稿的
    「落點標記」（資訊），這個是有厚度的實心帶（特效）。
    厚帶 + 一般混色 = 它會**遮住**背後的東西 —— 那是「有實體」唯一的線索。 */
export function texPolyBand(n: number, th: number) {
  return mkTex('p2band' + n + '_' + th, 256, 256, function (d, w) {
    var r = w / 2, aa = 1.6 / r;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var rp = polyR(n, Math.atan2(dy, dx)) * 0.96;
      var a = Math.min(edge(rp - rad, aa), edge(rad - (rp - th), aa));
      putA(d, (y * w + x) * 4, a);
    }
  });
}

/** 三角碎片：不對稱、尖端朝 +x。**這是 A 方向的記憶點。**

    對稱的形狀讀成符號（法陣、標記），不對稱的才讀成「被打下來的一塊」。
    刻意做成一整片實心 —— 它走一般混色，所以飛過敵人身上時會**擋住敵人**，
    那一瞬間的遮擋就是「有東西被打掉了」。 */
export function texShard() {
  return mkTex('p2shard', 96, 96, function (d, w) {
    var r = w / 2, aa = 1.4 / r;
    var A = [0.96, 0.04], B = [-0.78, -0.62], C = [-0.52, 0.72];
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var px = (x + 0.5 - r) / r, py = (y + 0.5 - r) / r;
      /* 點到三條邊的有號距離取最小 —— 正值在三角形內側。
         用距離而不是「內外判斷」，是為了那一格的抗鋸齒過渡。 */
      var e = Math.min(seg(px, py, A, B), Math.min(seg(px, py, B, C), seg(px, py, C, A)));
      putA(d, (y * w + x) * 4, edge(e, aa));
    }
  });
}
/** 點到有向線段 (p→q) 左側的距離（左側為正）。 */
function seg(x: number, y: number, p: number[], q: number[]) {
  var ex = q[0] - p[0], ey = q[1] - p[1];
  var L = Math.hypot(ex, ey);
  return ((x - p[0]) * ey - (y - p[1]) * ex) / L;
}

/** 針狀切片：兩端都收成尖點的細長薄片，中段最寬。

    **D 方向的基本筆畫。** 能量武器的問題從來不是冷色，是**沒有邊界** ——
    模糊的光暈不管什麼顏色都是魔法。給它一個銳利的輪廓，同一個青色
    就會讀成「一片被切出來的電漿」。 */
export function texNeedle() {
  return mkTex('p2needle', 256, 48, function (d, w, h) {
    var aa = 1.4 / (h / 2);
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
      var u = (x + 0.5) / w, v = (y + 0.5) / h * 2 - 1;
      // 兩端尖：半寬沿長度是一條被壓扁的正弦，次方 < 1 讓中段飽滿、端點更尖
      var half = Math.pow(Math.sin(Math.PI * u), 0.55);
      putA(d, (y * w + x) * 4, edge(half - Math.abs(v), aa));
    }
  });
}

/** 環帶扇形：**判定範圍本人**。

    ⚠️ 這張貼圖存在的理由是一個實際的謊。環繞旋刃原本畫的是
    「n 片薄刃 + 一圈六角框」，而判定是「半徑差 < w、角差 < span」——
    兩者對不上，於是畫面上會同時出現兩種錯覺：

    - 六角框是 `polyR(6,θ)*0.96`，邊中點只有 0.83R、頂點才 0.96R，
      **框比判定圈小 4–17%** —— 刀刃在自己的框外面掃。
    - 一片刀只覆蓋整圈的 17.5%，但畫面上看不出「沒被掃到的那 82%」，
      所以敵人穿過去不掉血會讀成「這把武裝壞了」。

    改法是**把判定畫出來**：內外半徑就是判定的半徑帶，張角就是判定的角差。
    看得到的形狀 = 會受傷的區域，沒有第二種解釋。

    `spanFrac` = 半張角 / π（1 = 整圈）。`inner` = 內半徑 / 外半徑。
    填充壓在 0.42、邊界與前緣拉到 1.0 —— 硬邊的輪廓仍然在，
    但中間是半透明的，不會把腳下的地面整塊蓋掉。 */
export function texArc(spanFrac: number, inner: number) {
  return mkTex('arc' + spanFrac.toFixed(3) + '_' + inner.toFixed(2), 256, 256, function (d, w) {
    var r = w / 2, aa = 1.6 / r;
    var span = Math.min(Math.PI, spanFrac * Math.PI);
    var full = spanFrac >= 0.999;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var i4 = (y * w + x) * 4;
      var band = Math.min(edge(0.98 - rad, aa), edge(rad - inner, aa));
      if (band <= 0) { putA(d, i4, 0); continue; }
      var th = Math.atan2(dy, dx);
      var wedge = full ? 1 : edge(span - Math.abs(th), 0.02);
      if (wedge <= 0) { putA(d, i4, 0); continue; }
      /* 邊界（內外緣、扇形的兩側）與**前緣**拉到不透明 ——
         前緣那一道就是「刀口剛剛掃過這裡」，方向感全靠它。 */
      var lip = Math.max(
        edge(0.035 - Math.abs(rad - 0.98), aa),
        edge(0.035 - Math.abs(rad - inner), aa));
      if (!full) {
        lip = Math.max(lip, edge(0.04 - Math.abs(Math.abs(th) - span), 0.02));
        lip = Math.max(lip, edge(0.18 * span - Math.abs(th - span), 0.02));
      }
      putA(d, i4, band * wedge * (0.42 + 0.58 * lip));
    }
  });
}

/* ---------- 賽璐珞組 ---------- */

/** 手繪感的爆炸剪影，三張一組（`i` = 0/1/2）。**B 方向的記憶點。**

    這不是粒子，是**一張畫**：邊界沿角度不規則起伏（像用筆一筆畫出來的），
    內部全滿。三張形狀不同、由呼叫端依序播放，靠「換形狀」表達爆炸的推進，
    不靠「變大變淡」。這就是格鬥遊戲命中拍的作法 ——
    打擊感來自幀，不來自光。 */
export function texCel(i: number) {
  return mkTex('p2cel' + i, 128, 128, function (d, w) {
    var r = w / 2, aa = 1.5 / r;
    var s = i * 31 + 7;
    // 每一張用三個不同頻率的正弦疊出邊界，相位由固定亂數決定
    var f1 = 3 + ((hash(s) * 3) | 0), f2 = 5 + ((hash(s + 1) * 4) | 0);
    var p1 = hash(s + 2) * 6.283, p2 = hash(s + 3) * 6.283;
    var amp = 0.20 + i * 0.06;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      var th = Math.atan2(dy, dx);
      var rp = 0.72 + amp * Math.sin(th * f1 + p1) + amp * 0.55 * Math.sin(th * f2 + p2);
      putA(d, (y * w + x) * 4, edge(rp - rad, aa));
    }
  });
}

/** 命中拍的弧：一道厚度不均的新月。

    正面命中的那一格，格鬥遊戲畫的不是圓也不是星，是**一道弧** ——
    它同時表達「打到了」與「力的方向」。用兩個偏心圓相減得到，
    厚度自然沿弧長變化（中間厚、兩端尖），這正是手繪的味道。 */
export function texCelArc() {
  return mkTex('p2arc', 192, 192, function (d, w) {
    var r = w / 2, aa = 1.5 / r;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var out = 0.95 - Math.sqrt(dx * dx + dy * dy);
      var ix = dx + 0.34, iy = dy;
      var inn = Math.sqrt(ix * ix + iy * iy) - 0.80;
      putA(d, (y * w + x) * 4, Math.min(edge(out, aa), edge(inn, aa)));
    }
  });
}

/** 集中線：由中心放射的直線，內側留空。

    暴擊那一格才出現。它完全不是「特效」而是**漫畫的語法** ——
    畫面上一出現集中線，讀者就知道「這一下很重」，不需要任何亮度。
    內側留空是為了不遮住命中點本身。 */
export function texLines(n: number) {
  return mkTex('p2lines' + n, 256, 256, function (d, w) {
    var r = w / 2, aa = 2.0 / r;
    for (var y = 0; y < w; y++) for (var x = 0; x < w; x++) {
      var dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
      var rad = Math.sqrt(dx * dx + dy * dy);
      if (rad > 0.99 || rad < 0.30) { putA(d, (y * w + x) * 4, 0); continue; }
      var th = Math.atan2(dy, dx);
      var f = (th + Math.PI) / (Math.PI * 2) * n;
      var frac = Math.abs(f - Math.floor(f) - 0.5) * 2;      // 0 = 線心, 1 = 線間
      /* 線寬沿半徑收窄（外粗內細），讀成「往內收斂」而不是一圈柵欄。
         尾端另外淡出一格，否則外圈會出現一道生硬的截斷。 */
      var half = 0.34 * rad;
      var a = edge(half - frac * 0.5, aa) * edge(0.99 - rad, 0.06);
      putA(d, (y * w + x) * 4, a);
    }
  });
}
